<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">{{ __('Termékek') }}</h1>

        <div class="flex items-center mb-8">
            <c-control-text class="w-full" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none"
                            icon="search"
                            name="search"
                            v-bind:placeholder="__('Keresés')"
                            v-model="s"
                            v-on:keyup="search"
            />

            <c-button class="bg-slate-800 ml-4 shrink-0 text-white"
                      router
                      v-bind:to="{ name : 'products.create' }"
            >
                + {{ __('Új termék') }}
            </c-button>
        </div>

        <c-data-table responsive-breakpoint="lg"
                      v-bind:data="tableData"
                      v-if="tableData.items.length > 0"
                      v-on:sort-table="sortTable"
        >
            <template v-slot:item.picture="{ item }">
                <router-link v-bind:to="{ name: 'products.edit', params: { product: item.values.id } }">
                    <img class="max-w-32" v-bind:alt="item.values.name" v-bind:src="item.values.picture" />
                </router-link>
            </template>
            <template v-slot:item.name="{ item }">
                <router-link class="hover:decoration-dotted hover:underline"
                             v-bind:to="{ name: 'products.edit', params: { product: item.values.id } }"
                >
                    <strong class="block text-slate-800 text-sm">{{ item.values.name }}</strong>
                    <span class="text-slate-500 text-xs">{{ item.values.description }}</span>
                </router-link>
            </template>
            <template v-slot:item.price_gross="{ item }">
                {{ currencyStore.format(item.values.price_gross, 'huf') }}
            </template>
            <template v-slot:item.is_active="{ item }">
                <div class="flex items-center justify-center">
                    <c-icon class="h-6 stroke-success w-6" icon="check-circle" v-if="item.values.is_active" />
                    <c-icon class="h-6 stroke-error w-6" icon="x-square" v-else />
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <div class="flex flex-col">
                    <router-link class="cursor-pointer flex hover:decoration-dotted hover:underline text-slate-800 text-xs"
                                 v-bind:to="{ name: 'products.edit', params: { product: item.values.id } }"
                    >
                        <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="edit-3" />
                        {{ __('Szerkesztés') }}
                    </router-link>
                    <a class="cursor-pointer flex hover:decoration-dotted hover:underline mt-2 text-slate-800 text-xs"
                       v-on:click="deletedModel = item.values.id"
                    >
                        <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="trash-2" />
                        {{ __('Törlés') }}
                    </a>
                </div>
            </template>
        </c-data-table>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>

    <c-modal class="md:w-[450px]" v-bind:open="deletedModel !== null" v-bind:title="__('Termék törlése')" v-on:closeModal="deletedModel = null">
        <template v-slot:body>
            <div class="py-5 text-center text-sm">
                {{ __('Biztosan törölni szeretnéd ezt a terméket?') }}
            </div>
        </template>
        <template v-slot:footer>
            <div class="flex justify-end w-full">
                <c-button class="bg-slate-100 mr-2 text-slate-800" v-on:click="deletedModel = null">
                    {{ __('Mégsem') }}
                </c-button>

                <c-button class="bg-error text-white" v-on:click="del">
                    {{ __('Törlés') }}
                </c-button>
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
    import { inject, onMounted, ref } from 'vue';
    import {
        CButton,
        CControlText,
        CDataTable,
        CIcon,
        CModal,
    } from '@teamfurther/cinderblock';
    import debounce from 'debounce';
    import ProductRepository from '../../repositories/ProductRepository';
    import { useCurrencyStore } from '../../stores/currency';

    const __ = inject('__');
    const currencyStore = useCurrencyStore();
    const deletedModel = ref(null);
    const productRepository = new ProductRepository();
    const s = ref<string>('');
    const sort = ref<string>('name');
    const tableData = ref<object>({
        columnAligns: { is_active: 'center' },
        columnNames: {
            picture: __('Kép'),
            name: __('Megnevezés'),
            price_gross: __('Ár (bruttó)'),
            is_active: __('Aktív?'),
            actions: __('Műveletek'),
        },
        defaultSort: '+name',
        items: [],
        sortableColumns: ['name', 'price_gross'],
    });

    async function del() {
        await productRepository.destroy(deletedModel.value);

        await getProducts();

        deletedModel.value = null;
    }

    async function getProducts() {
        const result = await productRepository.index({
            filters: { name: s.value },
            sort: sort.value,
        });

        tableData.value.items = result.map((product) => {
            return { values : product }
        });
    }

    const search = debounce(async () => {
        await getProducts();
    }, 500);

    async function sortTable(sortBy: string) {
        sort.value = sortBy;

        await getProducts();
    }

    onMounted(async () => {
        await getProducts();
    });
</script>
